import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

import {
  Amount,
  OptionalExercise,
  ParentProgrammeGeneralDetails,
  Programme,
  RecommendedExercise
} from '../../../../../types/entity.types';

import { ARCHIVED, UPCOMING } from '../../../../../utils/constants';
import { getClassNames, getWeekOptions } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import Dropdown from '../../../../common/input/dropdown/Dropdown';

import { ReactComponent as CloseIcon } from '../../../../../resources/icons/close.svg';
import { ReactComponent as TrashIcon } from '../../../../../resources/icons/trash.svg';

import '../accountability.scss';
import './accountability-movement.scss';

type Props = {
  status: string;
  programme?: Programme;
  parentProgramme?: ParentProgrammeGeneralDetails;
  recommendedExercises: RecommendedExercise[];
  optionalExercises: OptionalExercise[];
  createRecommendedExercise: () => void;
  updateRecommendedExercise: (
    externalReference: string,
    key: keyof Omit<RecommendedExercise, 'amounts'>,
    value: string
  ) => void;
  deleteRecommendedExercise: (externalReference: string) => void;
  createExerciseAmount: (exerciseExternalReference: string) => void;
  updateExerciseAmount: (
    exerciseExternalReference: string,
    amountExternalReference: string,
    key: keyof Amount,
    value: string
  ) => void;
  deleteExerciseAmount: (exerciseExternalReference: string, amountExternalReference: string) => void;
  createOptionalExercise: () => void;
  updateOptionalExercise: (externalReference: string, key: keyof OptionalExercise, value: string) => void;
  deleteOptionalExercise: (externalReference: string) => void;
};

const AccountabilityMovement = ({
  status,
  programme,
  parentProgramme,
  recommendedExercises = [],
  optionalExercises = [],
  createRecommendedExercise,
  updateRecommendedExercise,
  deleteRecommendedExercise,
  createExerciseAmount,
  updateExerciseAmount,
  deleteExerciseAmount,
  createOptionalExercise,
  updateOptionalExercise,
  deleteOptionalExercise
}: Props) => {
  const [open, setOpen] = useState(true);

  const renderAmounts = (exercise: RecommendedExercise) => {
    const { externalReference, amounts } = exercise;

    const options = getWeekOptions(programme, parentProgramme);

    return (
      <div className="accountability-movement__recommended-exercise__amount">
        <div className="accountability-movement__recommended-exercise__amount__headers">
          <p>From Week:</p>
          <p>To Week:</p>
          <p>Daily Amount:</p>
        </div>
        {amounts.map((amount) => (
          <div
            className="accountability-movement__recommended-exercise__amount__inputs"
            key={`${externalReference}-${amount.externalReference}`}
            data-testid="recommended-exercise-amount"
          >
            <Dropdown
              value={{ label: amount.fromWeek, value: amount.fromWeek }}
              options={options.filter((option) => !amount.toWeek || Number(option.value) <= Number(amount.toWeek))}
              onChange={(option) =>
                updateExerciseAmount(externalReference, amount.externalReference, 'fromWeek', option.value)
              }
              disabled={status === ARCHIVED}
            />
            <Dropdown
              value={{ label: amount.toWeek, value: amount.toWeek }}
              options={options.filter((option) => !amount.fromWeek || Number(option.value) >= Number(amount.fromWeek))}
              onChange={(option) =>
                updateExerciseAmount(externalReference, amount.externalReference, 'toWeek', option.value)
              }
              disabled={status === ARCHIVED}
            />
            <div>
              <input
                className="accountability-movement__input"
                value={amount.amount}
                onChange={({ target }) =>
                  updateExerciseAmount(externalReference, amount.externalReference, 'amount', target.value)
                }
                disabled={status === ARCHIVED}
              />
              {status !== ARCHIVED && (
                <FontAwesomeIcon
                  className="accountability-movement__recommended-exercise__amount__add-icon"
                  icon={faPlus}
                  onClick={() => createExerciseAmount(externalReference)}
                  data-testid="create-amount-icon"
                />
              )}
              {status !== ARCHIVED && amounts.length > 1 && (
                <TrashIcon
                  className="accountability-movement__recommended-exercise__amount__trash-icon"
                  onClick={() => deleteExerciseAmount(externalReference, amount.externalReference)}
                  data-testid="delete-amount-icon"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRecommendedExercises = () => {
    return (
      <div className="accountability-movement__recommended-exercises">
        {recommendedExercises.map((exercise, index) => (
          <div className="accountability-movement__recommended-exercise" key={index} data-testid="recommended-exercise">
            <div className="accountability-movement__recommended-exercise__headers">
              <p>Recommended Exercise:</p>
              <p>Measurement:</p>
            </div>
            <div className="accountability-movement__recommended-exercise__inputs">
              <input
                className="accountability-movement__input"
                value={exercise.name}
                onChange={({ target }) => updateRecommendedExercise(exercise.externalReference, 'name', target.value)}
                disabled={status === ARCHIVED}
              />
              <input
                className="accountability-movement__input"
                value={exercise.measurement}
                onChange={({ target }) =>
                  updateRecommendedExercise(exercise.externalReference, 'measurement', target.value)
                }
                disabled={status === ARCHIVED}
              />
            </div>
            {renderAmounts(exercise)}
            {status === UPCOMING && recommendedExercises.length > 1 && (
              <div className="accountability-movement__recommended-exercise__close-icon">
                <CloseIcon
                  className="accountability-movement__close-icon"
                  onClick={() => deleteRecommendedExercise(exercise.externalReference)}
                  data-testid="recommended-delete-icon"
                />
              </div>
            )}
          </div>
        ))}
        {status !== ARCHIVED && <Button text="Add" onClick={createRecommendedExercise} />}
      </div>
    );
  };

  const renderOptionalExercises = () => (
    <div className="accountability-movement__optional-exercises">
      <div>
        <p>Optional Exercise:</p>
        <p>Measurement:</p>
      </div>
      {optionalExercises.map((exercise, index) => (
        <div key={index} data-testid="optional-exercise">
          <input
            className="accountability-movement__input"
            value={exercise.name}
            onChange={({ target }) => updateOptionalExercise(exercise.externalReference, 'name', target.value)}
            disabled={status === ARCHIVED}
          />
          <div>
            <input
              className="accountability-movement__input"
              value={exercise.measurement}
              onChange={({ target }) => updateOptionalExercise(exercise.externalReference, 'measurement', target.value)}
              disabled={status === ARCHIVED}
            />
            {status === UPCOMING && optionalExercises.length > 1 && (
              <CloseIcon
                className="accountability-movement__close-icon"
                onClick={() => deleteOptionalExercise(exercise.externalReference)}
                data-testid="optional-delete-icon"
              />
            )}
          </div>
        </div>
      ))}
      {status !== ARCHIVED && <Button text="Add" onClick={createOptionalExercise} />}
    </div>
  );

  return (
    <div
      className={getClassNames('accountability__section', {
        closed: !open
      })}
      data-testid="accountability-movement"
    >
      <div className="accountability__section__header">
        <p>MOVEMENT</p>
        {!open && (
          <FontAwesomeIcon
            className="accountability__section__open-icon"
            icon={faPlus}
            onClick={() => setOpen(true)}
            data-testid="open-icon"
          />
        )}
        {open && (
          <FontAwesomeIcon
            className="accountability__section__close-icon"
            icon={faMinus}
            onClick={() => setOpen(false)}
            data-testid="close-icon"
          />
        )}
      </div>
      {open && (
        <div className="accountability-movement">
          {renderRecommendedExercises()}
          {renderOptionalExercises()}
        </div>
      )}
    </div>
  );
};

export default AccountabilityMovement;
